import { ResolverTag } from '@ps/web-core-enums';
import type { TrackedTranslation } from '@ps/web-core-intl';
import { getWindow } from '@ps/web-core-utils';
import { RawButton, StarsButton, Translation } from '@ps/web-shared-components';
import { StarsInfo } from '@ps/web-shared-ui';
import classNames from 'clsx';
import { isUndefined } from 'lodash';
import React, { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ResolverCMSContent } from '@swf/cms';
import Body from '@swf/components/StarsModal/components/Body';
import CloseButton from '@swf/components/StarsModal/components/CloseButton';
import Header from '@swf/components/StarsModal/components/Header/Header';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';

import {
  TXTCLI_Back,
  TXTWCL_Accept_Terms_X_Conditions,
  TXTWCL_HereXs_how_to_take_part,
  TXTWCL_Terms_and_Conditions,
  TXTWCL_The_Spin_of_the_Day_Terms_X_Co_ELL,
  TXTWCL_XfirstXThe_Races_Terms_X_Condi_ELL
} from './CasinoHelp.messages.json';
import styles from './CasinoHelp.scss';

interface CasinoHelpParams {
  title: string;
  buttonText: string;
  buttonTextTag?: TrackedTranslation;
  acceptanceRequired: boolean;
  version: number;
  pathname?: string;
  isSotd?: boolean;
  onClose?: () => void;
}

const CasinoHelp: React.FC = () => {
  const {
    title,
    buttonText,
    buttonTextTag,
    acceptanceRequired,
    version,
    pathname,
    isSotd,
    onClose
  } = useSearchParams<CasinoHelpParams>();
  const { casino, modal } = useDispatch<Dispatch>();
  const isMobile = useSelector(select.device.isMobile);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const iOSEmbedded = useSelector(select.device.isIOSEmbedded);

  const gameConfig = {
    resolverTerms: isSotd ? ResolverTag.PRMSotdTerms : ResolverTag.PRMCasinoRacesIngame,
    reacceptTranslation: isSotd
      ? TXTWCL_The_Spin_of_the_Day_Terms_X_Co_ELL
      : TXTWCL_XfirstXThe_Races_Terms_X_Condi_ELL,
    legacyLiveAccepted: useSelector(select.casino.liveRacesTermsAccepted),
    legacyUKAccepted: useSelector(select.casino.UKTermsAccepted),
    acceptedVersion: useSelector(
      isSotd ? select.casino.sotdTermsVersionAccepted : select.casino.racesTermsVersionAccepted
    )
  };
  const haveAcceptedAnyTerms =
    gameConfig.legacyLiveAccepted ||
    gameConfig.legacyUKAccepted ||
    !isUndefined(gameConfig.acceptedVersion);
  const showReacceptMsg = acceptanceRequired && haveAcceptedAnyTerms;
  const text = (text: ReactNode) => <span className={styles.ownLine}>{text}</span>;

  const handleAccept = useCallback(() => {
    isSotd ? casino.acceptSotdTerms(version) : casino.acceptRacesTerms(version);
  }, [isSotd, casino, version]);

  return (
    <>
      <Header>
        {acceptanceRequired ? (
          <Translation {...TXTWCL_Terms_and_Conditions} />
        ) : (
          title || <Translation {...TXTWCL_HereXs_how_to_take_part} />
        )}
        <CloseButton onClick={() => onClose?.()} />
      </Header>
      <Body
        className={classNames(styles.body, {
          [styles.topPadding]: !showReacceptMsg,
          [styles.fullHeightEmbedded]: isDesktopEmbedded,
          [styles.mobileDevice]: isMobile,
          [styles.paddingBottom]: iOSEmbedded && !getWindow()?.starsweb?.mystars?.noBottomPadding
        })}
      >
        {showReacceptMsg && (
          <StarsInfo className={styles.info}>
            <Translation
              {...gameConfig.reacceptTranslation}
              values={{ first: text, second: text }}
            />
          </StarsInfo>
        )}
        <div className={styles.scrollableArea}>
          <ResolverCMSContent tag={gameConfig.resolverTerms} pathname={pathname} />
        </div>
        {acceptanceRequired ? (
          <StarsButton
            block
            onClick={handleAccept}
            translation={TXTWCL_Accept_Terms_X_Conditions}
          />
        ) : (
          <RawButton
            data-testid="terms-and-conditions-back-button-on-races-tab"
            className={styles.button}
            block
            onClick={() => modal.close()}
          >
            {buttonText || <Translation {...(buttonTextTag || TXTCLI_Back)} />}
          </RawButton>
        )}
      </Body>
    </>
  );
};

export default CasinoHelp;
